import React, { useState, useRef, useEffect } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { messagesAPI } from '../../utils/crmApi';
import {
    PaperAirplaneIcon, PaperClipIcon, XMarkIcon, UserIcon,
    CheckIcon, CheckCircleIcon, ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import toast from 'react-hot-toast';

const MessageComposer = ({ 
    conversationId, 
    recipient, 
    onMessageSent, 
    onClose,
    isModal = false 
}) => {
    const { branding } = useBranding();
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    const personalizedColors = {
        primary: branding?.primaryColor || '#3B82F6',
        secondary: branding?.secondaryColor || '#1E40AF',
        accent: branding?.accentColor || '#F59E0B'
    };

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    }, []);

    const handleSendMessage = async () => {
        if (!message.trim() || sending) return;

        try {
            setSending(true);
            
            const messageData = {
                content: message.trim(),
                conversationId,
                recipientId: recipient?.id,
                recipientModel: recipient?.model || 'Client'
            };

            // Add attachments if any
            if (attachments.length > 0) {
                messageData.attachments = attachments.map(file => ({
                    fileName: file.name,
                    fileType: file.type,
                    fileSize: file.size
                }));
            }

            const res = await messagesAPI.sendMessage(messageData);
            if (res.data?.success) {
                setMessage('');
                setAttachments([]);
                toast.success('Message sent');
                onMessageSent?.(res.data.data);
            } else {
                throw new Error(res.data?.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file => {
            // Basic file validation
            const maxSize = 10 * 1024 * 1024; // 10MB
            if (file.size > maxSize) {
                toast.error(`File ${file.name} is too large. Maximum size is 10MB.`);
                return false;
            }
            return true;
        });

        setAttachments(prev => [...prev, ...validFiles]);
        e.target.value = ''; // Reset file input
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileType) => {
        if (fileType.startsWith('image/')) return '🖼️';
        if (fileType.includes('pdf')) return '📄';
        if (fileType.includes('word') || fileType.includes('document')) return '📝';
        if (fileType.includes('spreadsheet') || fileType.includes('excel')) return '📊';
        if (fileType.includes('presentation') || fileType.includes('powerpoint')) return '📈';
        return '📎';
    };

    if (isModal) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
                <div className="bg-card rounded-lg p-6 w-full max-w-md">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-foreground">Send Message</h3>
                        <button
                            onClick={onClose}
                            className="p-1 text-muted-foreground hover:text-muted-foreground"
                        >
                            <XMarkIcon className="h-5 w-5" />
                        </button>
                    </div>

                    {recipient && (
                        <div className="flex items-center space-x-2 mb-4 p-2 bg-muted rounded-lg">
                            <UserIcon className="h-5 w-5 text-muted-foreground" />
                            <div>
                                <p className="text-sm font-medium text-foreground">{recipient.name}</p>
                                <p className="text-xs text-muted-foreground">{recipient.email}</p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <textarea
                                ref={textareaRef}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Type your message..."
                                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring resize-none"
                                rows="3"
                                disabled={sending}
                            />
                        </div>

                        {/* Attachments */}
                        {attachments.length > 0 && (
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-foreground">Attachments:</p>
                                <div className="space-y-1">
                                    {attachments.map((file, index) => (
                                        <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-lg">{getFileIcon(file.type)}</span>
                                                <div>
                                                    <p className="text-sm font-medium text-foreground">{file.name}</p>
                                                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeAttachment(index)}
                                                className="p-1 text-muted-foreground hover:text-red-500"
                                            >
                                                <XMarkIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                                />
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="p-2 text-muted-foreground hover:text-muted-foreground"
                                    title="Attach files"
                                >
                                    <PaperClipIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="flex space-x-2">
                                <Button
                                    onClick={onClose}
                                    variant="outline"
                                    disabled={sending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSendMessage}
                                    disabled={!message.trim() || sending}
                                    style={{ backgroundColor: personalizedColors.primary }}
                                >
                                    {sending ? (
                                        <div className="flex items-center space-x-2">
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            <span>Sending...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center space-x-2">
                                            <PaperAirplaneIcon className="h-4 w-4" />
                                            <span>Send</span>
                                        </div>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="border-t border-border bg-muted p-4">
            {/* Read-only recipient display when provided */}
            {recipient && (
                <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">To:</span>
                    <div className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-card border border-border">
                        <UserIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate max-w-[200px]">{recipient.name}</span>
                        <span className="text-muted-foreground">•</span>
                        <span className="truncate max-w-[200px]">{recipient.email}</span>
                    </div>
                </div>
            )}
            {/* Attachments Preview */}
            {attachments.length > 0 && (
                <div className="mb-3 space-y-2">
                    <p className="text-sm font-medium text-foreground">Attachments:</p>
                    <div className="flex flex-wrap gap-2">
                        {attachments.map((file, index) => (
                            <div key={index} className="flex items-center space-x-2 p-2 bg-card rounded border">
                                <span className="text-lg">{getFileIcon(file.type)}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                                    <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                                </div>
                                <button
                                    onClick={() => removeAttachment(index)}
                                    className="p-1 text-muted-foreground hover:text-red-500"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex items-end space-x-2">
                <div className="flex-1 relative">
                    <textarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type your message..."
                        className="w-full px-3 py-2 pr-10 pb-8 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring resize-none"
                        rows="2"
                        disabled={sending}
                    />
                    <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="absolute bottom-2 left-2 p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                                title="Add emoji"
                                disabled={sending}
                            >
                                <FaceSmileIcon className="h-4 w-4" />
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 border-none shadow-lg" align="start" side="top">
                            <EmojiPicker
                                onEmojiClick={(emojiData) => {
                                    setMessage(prev => prev + emojiData.emoji);
                                    setShowEmojiPicker(false);
                                    textareaRef.current?.focus();
                                }}
                                theme="auto"
                                previewConfig={{ showPreview: false }}
                                skinTonesDisabled
                            />
                        </PopoverContent>
                    </Popover>
                </div>
                
                <div className="flex items-center space-x-1">
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors"
                        title="Attach files"
                        disabled={sending}
                    >
                        <PaperClipIcon className="h-5 w-5" />
                    </button>
                    
                    <Button
                        onClick={handleSendMessage}
                        disabled={!message.trim() || sending}
                        className="px-4 py-2"
                        style={{ backgroundColor: personalizedColors.primary }}
                    >
                        {sending ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <PaperAirplaneIcon className="h-4 w-4" />
                        )}
                    </Button>
                </div>
            </div>

            {/* Character count or other info */}
            <div className="flex justify-between items-center mt-2 text-xs text-muted-foreground">
                <span>Press Enter to send, Shift+Enter for new line</span>
                {message.length > 0 && (
                    <span className={message.length > 1000 ? 'text-red-500' : ''}>
                        {message.length}/1000
                    </span>
                )}
            </div>
        </div>
    );
};

export default MessageComposer;
